import { destroyCookie, parseCookies, setCookie } from 'nookies'

import {
  COOKIE_KEYS,
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS
} from 'utils/constants/common'

import { JwtPayload, decodeJwt } from './decodeJwt'

export type AuthPayload = {
  token: string
  idToken: string
  refreshToken?: string
  user: Record<string, any>
  forgerockId: string
  expires_in: string
  access_token: string
}

export type AuthState = {
  token: string | null
  idToken: string | null
  user: Record<string, any> | null
  isAuthenticated: boolean
  userId: string | null
}

type AuthJwtPayload = JwtPayload & {
  vrioCustomerId?: string
}

const isBrowser = () => typeof window !== 'undefined'
export const authAdapter = {
  // READ from storage
  getAuthFromStorage(): AuthState {
    if (!isBrowser()) {
      return {
        token: null,
        idToken: null,
        user: null,
        isAuthenticated: false,
        userId: null
      }
    }

    const cookies = parseCookies()
    const token = cookies[COOKIE_KEYS.TOKEN] ?? null

    let user: Record<string, any> | null = null
    let idToken: string | null = null
    let userId: string | null = null

    try {
      const userRaw = localStorage.getItem(LOCAL_STORAGE_KEYS.USER)
      user = userRaw ? JSON.parse(userRaw) : null
    } catch {
      user = null
    }

    try {
      /**
       * Fallback idToken to 'sessionToken', as used in the legacy buyflow.
       */
      const BUYFLOW_V2_VAR = localStorage.getItem(LOCAL_STORAGE_KEYS.ID_TOKEN)
      const BUYFLOW_V1_VAR = localStorage.getItem(
        LOCAL_STORAGE_KEYS.SESSION_TOKEN
      )

      idToken = BUYFLOW_V2_VAR ?? BUYFLOW_V1_VAR
    } catch {
      idToken = null
    }

    const decodedToken = idToken ? decodeJwt<AuthJwtPayload>(idToken) : null
    userId = decodedToken?.payload.vrioCustomerId ?? null

    // Use enhanced isAuthenticated method for validation
    const isAuthenticated = this.isAuthenticated()

    return {
      token,
      idToken,
      user,
      isAuthenticated,
      userId
    }
  },

  // WRITE to storage (retro-compatibility)
  setAuthToStorage(payload: AuthPayload) {
    if (!isBrowser()) return
    const {
      token,
      access_token,
      idToken,
      user,
      forgerockId,
      expires_in,
      refreshToken
    } = payload

    /* COOKIES - RETRO COMPATIBLE WITH LEGACY
     * Cookies expire in 1 day -implementation matching legacy, check if this should be the case-
     */
    const expires = new Date(Date.now() + 86400 * 1000)
    setCookie(null, COOKIE_KEYS.TOKEN, token, {
      path: '/',
      expires
    })

    setCookie(null, COOKIE_KEYS.FORGEROCK_ID, forgerockId, {
      path: '/',
      expires
    })

    /**
     * LOCAL STORAGE - RETRO COMPATIBLE WITH LEGACY
     */
    localStorage.setItem(LOCAL_STORAGE_KEYS.USER, JSON.stringify(user))
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.RENEW_ENTITLEMENTS,
      JSON.stringify({
        expireDate: user?.exp
          ? user.exp * 1000
          : Date.now() + Number(expires_in) * 1000,
        refresh_token: refreshToken
      })
    )
    localStorage.setItem(LOCAL_STORAGE_KEYS.IS_OTT_TBX, 'true')
    localStorage.setItem(LOCAL_STORAGE_KEYS.SESSION_TOKEN, idToken)
    // Stored as well as 'id_token' explicitly for Buyflow 2.0 usage to avoid cognitive load
    localStorage.setItem(LOCAL_STORAGE_KEYS.ID_TOKEN, idToken)
    // Legacy saves the id_token in local storage as 'frToken', not id_token
    if (access_token) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.FR_TOKEN, access_token)
    }

    /**
     * SESSION STORAGE - RETRO COMPATIBLE WITH LEGACY
     */
    // forgerockId is the vrio_id
    sessionStorage.setItem(SESSION_STORAGE_KEYS.USER_ID, forgerockId)
  },

  // CLEAR everything
  clearAuthStorage() {
    if (!isBrowser()) return

    // cookies
    destroyCookie(null, COOKIE_KEYS.TOKEN, { path: '/' })
    destroyCookie(null, COOKIE_KEYS.FORGEROCK_ID, { path: '/' })

    // localStorage
    localStorage.removeItem(LOCAL_STORAGE_KEYS.USER)
    localStorage.removeItem(LOCAL_STORAGE_KEYS.FR_TOKEN)
    localStorage.removeItem(LOCAL_STORAGE_KEYS.ID_TOKEN)
  },

  // Quick check with expiration validation
  isAuthenticated(): boolean {
    if (!isBrowser()) return false

    const cookies = parseCookies()
    const token = cookies[COOKIE_KEYS.TOKEN]

    const user = localStorage.getItem(LOCAL_STORAGE_KEYS.USER)

    if (!token || !user) {
      return false
    }

    // Check expiration using renewEntitlements
    try {
      const renewEntitlementsRaw = localStorage.getItem(
        LOCAL_STORAGE_KEYS.RENEW_ENTITLEMENTS
      )
      if (renewEntitlementsRaw) {
        const renewEntitlements = JSON.parse(renewEntitlementsRaw)
        if (renewEntitlements.expireDate) {
          const currentTime = Date.now()
          const expirationTime = renewEntitlements.expireDate

          // If current time is past expiration, return false
          if (currentTime >= expirationTime) {
            return false
          }
        }
      }
    } catch (error) {
      // If we can't parse or check expiration, fall back to basic check
      // This maintains backward compatibility
    }

    return true
  }
}
